import { useState, useEffect } from 'react';
import type { BreakpointKey, ResponsiveValue } from '@/utils/ui/breakpoint-utils';
import { breakpointUtils, resolveResponsiveValue } from '@/utils/ui/breakpoint-utils';

export interface UseBreakpointReturn {
  current: BreakpointKey;
  width: number;
  height: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isAtLeast: (breakpoint: BreakpointKey) => boolean;
  isAtMost: (breakpoint: BreakpointKey) => boolean;
  isBreakpoint: (breakpoint: BreakpointKey) => boolean;
  resolve: <T>(value: ResponsiveValue<T>) => T;
}

export const useBreakpoint = (): UseBreakpointReturn => {
  const [dimensions, setDimensions] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768
  }));

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    
    const handleResize = () => {
      // Debounce resize events to improve performance
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setDimensions({
          width: window.innerWidth,
          height: window.innerHeight
        });
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    
    // Set initial dimensions
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  const current = breakpointUtils.getCurrentBreakpoint(dimensions.width);
  const isMobile = breakpointUtils.isMobileBreakpoint(current);
  const isTablet = breakpointUtils.isTabletBreakpoint(current);
  const isDesktop = breakpointUtils.isDesktopBreakpoint(current);

  const isAtLeast = (breakpoint: BreakpointKey): boolean => {
    return breakpointUtils.isAtLeast(dimensions.width, breakpoint);
  };

  const isAtMost = (breakpoint: BreakpointKey): boolean => {
    return breakpointUtils.isAtMost(dimensions.width, breakpoint);
  };

  const isBreakpoint = (breakpoint: BreakpointKey): boolean => {
    return breakpointUtils.isBreakpoint(dimensions.width, breakpoint);
  };

  const resolve = <T>(value: ResponsiveValue<T>): T => {
    return resolveResponsiveValue(value, current);
  };

  return {
    current,
    width: dimensions.width,
    height: dimensions.height,
    isMobile,
    isTablet,
    isDesktop,
    isAtLeast,
    isAtMost,
    isBreakpoint,
    resolve
  };
};

// Specialized hooks for common breakpoint checks
export const useIsMobile = (): boolean => {
  const { isMobile } = useBreakpoint();
  return isMobile;
};

export const useIsTablet = (): boolean => {
  const { isTablet } = useBreakpoint();
  return isTablet;
};

export const useIsDesktop = (): boolean => {
  const { isDesktop } = useBreakpoint();
  return isDesktop;
};

export const useCurrentBreakpoint = (): BreakpointKey => {
  const { current } = useBreakpoint();
  return current;
};

export const useViewportSize = (): { width: number; height: number } => {
  const { width, height } = useBreakpoint();
  return { width, height };
};

// Hook for resolving responsive values
export const useResponsiveValue = <T>(value: ResponsiveValue<T>): T => {
  const { resolve } = useBreakpoint();
  return resolve(value);
};

// Hook for media query matching
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    // Initial state is already set in useState initializer

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
};

// Hook for checking if viewport is at least a certain breakpoint
export const useIsAtLeast = (breakpoint: BreakpointKey): boolean => {
  const { isAtLeast } = useBreakpoint();
  return isAtLeast(breakpoint);
};

// Hook for checking if viewport is at most a certain breakpoint
export const useIsAtMost = (breakpoint: BreakpointKey): boolean => {
  const { isAtMost } = useBreakpoint();
  return isAtMost(breakpoint);
};

// Hook for checking if viewport matches a specific breakpoint
export const useIsBreakpoint = (breakpoint: BreakpointKey): boolean => {
  const { isBreakpoint } = useBreakpoint();
  return isBreakpoint(breakpoint);
};

// Hook for orientation detection
export const useOrientation = (): 'portrait' | 'landscape' => {
  const { width, height } = useBreakpoint();
  return width > height ? 'landscape' : 'portrait';
};

// Hook for aspect ratio calculation
export const useAspectRatio = (): number => {
  const { width, height } = useBreakpoint();
  return width / height;
};